import type { EntryConditions, HistoryPoint, Timeframe } from "../types";
import { formatNumber } from "../lib/format";
import { timeframeReadouts } from "../lib/signals";
import { Sparkline } from "./Sparkline";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { cn } from "@/lib/utils";

interface Props {
  data: EntryConditions | null;
  history: HistoryPoint[] | null;
}

export function RsiPanel({ data, history }: Props) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between space-y-0 border-b p-3">
        <CardTitle>RSI</CardTitle>
        <span className="text-[11px] text-muted-foreground">
          &lt; 40 buy · &gt; 60 sell
        </span>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-1.5 p-2">
        {data ? (
          timeframeReadouts(data).map((r) => (
            <RsiCell
              key={r.timeframe}
              timeframe={r.timeframe}
              rsi={r.rsi}
              zone={r.rsiZone}
              values={historyValues(history, `rsi_${r.timeframe}`)}
            />
          ))
        ) : (
          <div className="col-span-2 p-2 italic text-muted-foreground">
            Loading…
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function historyValues(
  history: HistoryPoint[] | null,
  key: `rsi_${Timeframe}`
): number[] {
  if (!history) return [];
  return history
    .map((p) => p[key])
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
}

function RsiCell({
  timeframe,
  rsi,
  zone,
  values,
}: {
  timeframe: Timeframe;
  rsi: number;
  zone: "buy" | "sell" | "neutral";
  values: number[];
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[32px_auto_minmax(0,1fr)] items-center gap-2 rounded-md border px-2 py-2 transition-colors",
        zone === "buy" && "border-buy/35 bg-buy/10",
        zone === "sell" && "border-sell/35 bg-sell/10",
        zone === "neutral" && "border-transparent bg-secondary"
      )}
    >
      <div className="font-mono-tabular text-[10px] text-muted-foreground">
        {timeframe}
      </div>
      <div
        className={cn(
          "font-mono-tabular text-lg font-semibold leading-none",
          zone === "buy" && "text-buy",
          zone === "sell" && "text-sell"
        )}
      >
        {formatNumber(rsi, 1)}
      </div>
      <Sparkline values={values} lowBand={40} highBand={60} height={28} />
    </div>
  );
}
